import { useCallback } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';

import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import Autocomplete from '@gef-ui/form-material/Autocomplete';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { RoundedSquare } from 'components/atoms/CustomSVGIcon';
import codelistProvider from 'components/decorators/codelistProvider';
import { codelists } from 'constants/codelists';
import { chooseColorBasedOnId } from 'features/views/semaphore/utils';

const SemaphoreAutoComplete = ({ semaphoreCodelist, fieldName }) => {
	const onlyVisibleSemaphoreCodelist = semaphoreCodelist.filter((item) => item.isVisible === 1);
	const formData = useFormData();

	const getOptionLabel = useCallback((option) => option.label, []);

	const renderOption = useCallback(
		(props, option) => (
			<Box component="li" {...props}>
				<Box sx={{ fontSize: '10px', mr: '20px' }}>
					<RoundedSquare color={chooseColorBasedOnId(option.value)} />
				</Box>
				{option.label}
			</Box>
		),
		[],
	);

	const renderInput = useCallback(
		(params) => {
			const startAdornment = (
				<InputAdornment position="start" sx={{ ml: 1 }}>
					<RoundedSquare color={chooseColorBasedOnId(formData?.semaphore)} />
				</InputAdornment>
			);

			return (
				<TextField
					{...params}
					label={getLabelMessage('semaphore:semaphore')}
					fullWidth
					slotProps={{
						input: {
							...params?.InputProps,
							...(formData?.semaphore && { startAdornment }),
						},

						inputLabel: {
							...params.InputLabelProps,
						},

						htmlInput: {
							...params.inputProps,
						},
					}}
				/>
			);
		},
		[formData?.semaphore],
	);

	return (
		<Autocomplete
			size="small"
			fieldName={fieldName}
			label={getLabelMessage('semaphore:semaphore')}
			options={onlyVisibleSemaphoreCodelist}
			labelPropName="description"
			getOptionLabel={getOptionLabel}
			renderOption={renderOption}
			renderInput={renderInput}
		/>
	);
};

SemaphoreAutoComplete.propTypes = {
	fieldName: PropTypes.string,
	semaphoreCodelist: PropTypes.array,
};

export default codelistProvider(codelists.semaphoreSegmentation, 'semaphoreCodelist')(SemaphoreAutoComplete);
